import React, { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

export default function Header() {
  const { lang, toggleLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const navItems = [
    { label: t.nav.services, id: "services" },
    { label: t.nav.serviceArea, id: "service-area" },
    { label: t.nav.testimonials, id: "testimonials" },
    { label: t.nav.about, id: "about" },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0B0A08]/90 backdrop-blur-xl border-b border-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <button
            data-testid="logo-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2.5 group"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-shadow duration-300">
              <svg viewBox="0 0 24 24" fill="none" className="w-4.5 h-4.5" stroke="#000" strokeWidth="2.5">
                <path d="M12 2a4 4 0 0 1 4 4v2h-2V6a2 2 0 0 0-4 0v2H8V6a4 4 0 0 1 4-4z" />
                <rect x="6" y="10" width="12" height="12" rx="1" />
                <circle cx="12" cy="16" r="2" />
              </svg>
            </div>
            <span className="font-heading text-lg font-bold tracking-tight text-white">
              Houston <span className="text-gold-gradient">Locks</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => scrollTo(item.id)}
                className="text-sm font-medium text-white/50 hover:text-[#D4AF37] transition-colors duration-300"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="language-toggle"
              onClick={toggleLang}
              className="text-xs font-bold tracking-widest px-3 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300"
            >
              {lang === "en" ? "ES" : "EN"}
            </button>

            <button
              data-testid="header-cta"
              onClick={() => scrollTo("contact")}
              className="hidden sm:flex items-center gap-2 bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] text-black px-6 py-2.5 text-sm font-semibold rounded-full hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <Phone className="w-3.5 h-3.5" />
              {t.nav.requestService}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden p-2 text-white/70 hover:text-white"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#141311]/95 backdrop-blur-xl border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => scrollTo(item.id)}
                  className="block w-full text-left py-3 text-white/70 font-medium text-sm hover:text-[#D4AF37] transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-cta"
                onClick={() => scrollTo("contact")}
                className="w-full mt-2 bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] text-black px-5 py-3 text-sm font-semibold rounded-full"
              >
                {t.nav.requestService}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
